"use client";

import { Asterisk, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ScoopsOfJoy() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const testimonials = [{
    monogram: "@AM",
    handle: "@alex.m",
    quote: "Scoop brings the art of ice cream to life. Every flavor is a masterpiece of texture and taste!",
    author: "Alex M.",
    role: "Food Critic",
    timestamp: "3 DAYS AGO",
    tilt: "-rotate-3",
    tape: "top-3 -left-4 -rotate-12"
  }, {
    monogram: "@SJ",
    handle: "@sarah.j",
    quote: "The most sophisticated ice cream I have ever tasted. Truly an artisanal experience.",
    author: "Sarah J.",
    role: "Culinary Enthusiast",
    timestamp: "5 DAYS AGO",
    tilt: "rotate-3",
    tape: "top-4 -right-4 rotate-12"
  }, {
    monogram: "@KL",
    handle: "@kevin.l",
    quote: "Exceptional quality and a beautiful aesthetic. My go-to for premium frozen treats.",
    author: "Kevin L.",
    role: "Loyal Customer",
    timestamp: "1 WEEK AGO",
    tilt: "-rotate-2",
    tape: "-bottom-3 left-6 rotate-6"
  }, {
    monogram: "@MR",
    handle: "@maya.r",
    quote: "Every scoop feels like a celebration. The balance of flavors is simply perfect.",
    author: "Maya R.",
    role: "Pastry Chef",
    timestamp: "2 WEEKS AGO",
    tilt: "rotate-2",
    tape: "-top-3 right-8 -rotate-6"
  }];
  return <section data-section-id="1479" id="scoops-of-joy" className="relative overflow-hidden bg-background text-foreground py-24 md:py-36">
      {}
      <div className="absolute inset-x-0 top-0 h-[6px] bg-primary" />
      <div className="absolute top-[6px] left-1/2 -translate-x-1/2 flex gap-2">
        <span className="block size-2 rounded-lg bg-secondary" />
        <span className="block size-2 rounded-lg bg-secondary" />
        <span className="block size-2 rounded-lg bg-secondary" />
      </div>

      {}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')]" />

      <div className="relative mx-auto max-w-[1360px] px-6 md:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={rightRef} className={`order-1 lg:order-2 lg:col-span-5 transition-all duration-700 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-28">
              {}
              <div className="inline-flex items-center gap-2 -rotate-3 rounded-lg bg-primary px-4 py-2 shadow-md">
                <span className="size-2 rounded-lg bg-background" />
                <span className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-background">
                  Edition 01 — Pure Joy
                </span>
              </div>

              <h2 className="mt-8 font-default text-foreground font-bold leading-[0.95] tracking-tight text-4xl md:text-5xl lg:text-6xl">
                Artisanal{" "}
                <span className="relative inline-block italic text-primary">
                  Scoops
                  <svg aria-hidden viewBox="0 0 200 14" className="absolute -bottom-2 left-0 w-full h-3" preserveAspectRatio="none">
                    <path d="M2 7 Q 25 1, 50 7 T 100 7 T 150 7 T 198 7" stroke="hsl(0 100% 71%)" strokeWidth="3" fill="none" strokeLinecap="round" />
                  </svg>
                </span>
              </h2>

              <p className="mt-8 font-default text-base md:text-lg leading-relaxed text-foreground/80 max-w-md">
                Handcrafted frozen delights inspired by global flavors and local ingredients. Experience the perfect balance of creaminess and craft in every bite.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/ShopPage">
                  <Button variant="outline" className="group rounded-lg border-[1.5px] border-foreground bg-transparent px-7 py-6 font-mono text-xs uppercase tracking-[0.14em] text-foreground hover:bg-primary hover:text-white hover:border-primary transition-all duration-300">
                    <Plus className="size-4" />
                    Explore Flavors
                  </Button>
                </Link>
                <span className="font-mono text-xs uppercase tracking-[0.18em] text-foreground/60">
                  ◉ 500+ happy scoops
                </span>
              </div>

              {}
              <div className="hidden lg:block mt-16 relative h-24 w-24 opacity-60">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  {[...Array(7)].map((_, i) => <line key={i} data-index={i} x1="50" y1="50" x2={50 + 40 * Math.cos(i * Math.PI / 7)} y2={50 - 40 * Math.sin(i * Math.PI / 7)} stroke="hsl(0 100% 71%)" strokeWidth="2" strokeLinecap="round" />)}
                </svg>
              </div>
            </div>
          </div>

          {}
          <div ref={leftRef} className="order-2 lg:order-1 lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
              {testimonials.map((t, i) => <div key={i} data-index={i} className={`group relative transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${i % 2 === 1 ? "sm:mt-12" : ""}`} style={{
              transitionDelay: `${i * 100}ms`
            }}>
                  <div className={`relative ${t.tilt} group-hover:rotate-0 transition-transform duration-300 ease-out`}>
                    {}
                    <div className={`absolute ${t.tape} z-10 h-6 w-16 bg-secondary/70 shadow-sm`} />

                    <article className="relative overflow-hidden rounded-lg border border-foreground/10 bg-card shadow-lg">
                      {}
                      <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-primary group-hover:w-full transition-all duration-500 ease-out" />

                      {}
                      <header className="flex items-center justify-between gap-3 border-b border-foreground/10 bg-accent px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex size-10 items-center justify-center rounded-lg bg-foreground font-mono text-xs font-semibold tracking-tight text-background">
                            {t.monogram}
                          </div>
                          <div className="flex flex-col">
                            <span className="font-default text-sm font-semibold text-foreground leading-tight">
                              {t.author}
                            </span>
                            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                              {t.handle}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, s) => <Asterisk key={s} data-index={s} className="size-3.5 text-primary" strokeWidth={2.5} />)}
                        </div>
                      </header>

                      {}
                      <div className="relative px-5 py-6">
                        {}
                        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.05] bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%221.2%22 numOctaves=%222%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')]" />
                        <p className="relative font-default text-base leading-relaxed text-foreground">
                          {t.quote}
                        </p>
                        <p className="relative mt-4 font-mono text-[10px] uppercase tracking-[0.14em] text-primary">
                          — {t.role}
                        </p>
                      </div>

                      {}
                      <footer className="flex items-center justify-between border-t border-foreground/10 px-5 py-3">
                        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-foreground/60">
                          ◉ {t.timestamp}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-foreground/60">
                           ↺ 
                        </span>
                      </footer>
                    </article>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}